/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Organizations API
 * Scrivio API for organization management.  ## Authentication All endpoints require Firebase Authentication JWT token in `Authorization: Bearer <token>` header.  ## Error Responses All error responses follow RFC 7807 Problem Details format. 
 *
 * The version of the OpenAPI document: 1.0.0
 * Contact: support@scrivio.nl
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

/**
 * What a user holding `organization.settings.update` accepts on the practice\'s behalf when asked to accept a newly published version (#4494). 
 */
export interface AcceptAgreementsRequest {
    /**
     * Each document the prompt showed, at the version it showed. See `POST /api/v1/organizations/me/agreements` for what is refused. 
     */
    'acceptedAgreements': Array<AcceptedAgreement>;
}
/**
 * What the invitee accepts while joining (#4491). Only read when the invitation\'s `agreementsToAccept` is not empty; an invitation that needs no acceptance ignores it, so the body may be left out entirely. 
 */
export interface AcceptInvitationRequest {
    /**
     * Every document in the invitation\'s `agreementsToAccept`, at the version given there. A missing entry or any other version is refused with `400` — like `CreateOrganizationRequest.acceptedAgreements`, it is refused server-side rather than `required:` in the schema, so the refusal says which document and which version. 
     */
    'acceptedAgreements'?: Array<AcceptedAgreement>;
}
/**
 * Response after successfully accepting an invitation
 */
export interface AcceptInvitationResponse {
    /**
     * ID of the organization the user joined
     */
    'organizationId': string;
    /**
     * Name of the organization
     */
    'organizationName': string;
    /**
     * Name of the role assigned
     */
    'roleName': string;
}
/**
 * One agreement a practice accepts, and the version it accepted.
 */
export interface AcceptedAgreement {
    'document': AgreementDocument;
    /**
     * The published version accepted. Compared for equality against the version Scrivio currently publishes — never ordered, so no scheme is implied beyond \"these two strings are the same document revision\". 
     */
    'version': string;
}


/**
 * Stable per-behandelaar agenda palette assignment for an organization. Keys are behandelaar UUIDs; values are 0-based indices into the frontend colour palette (`behandelaarColors.ts`, 24 colours). Assignments are distinct within the org up to the palette size and never reshuffle. 
 */
export interface AgendaColorsResponse {
    /**
     * Map of behandelaar UUID (string) to palette index (0-based)
     */
    'colors': { [key: string]: number; };
}
/**
 * A Scrivio agreement a practice enters into. The two are versioned and accepted separately: a change to what Scrivio charges is a new Overeenkomst and leaves the Verwerkersovereenkomst untouched. 
 */

export const AgreementDocument = {
    Overeenkomst: 'OVEREENKOMST',
    Verwerkersovereenkomst: 'VERWERKERSOVEREENKOMST'
} as const;

export type AgreementDocument = typeof AgreementDocument[keyof typeof AgreementDocument];


/**
 * What an organisation is bound by for one agreement, and whether the version Scrivio currently publishes is still outstanding (#4489). 
 */
export interface AgreementStatus {
    'document': AgreementDocument;
    /**
     * The version in force. `null` when the organisation has accepted nothing, and also when what it is bound by is a contract signed outside Scrivio — that has no published version to name, which is why `currentVersionOutstanding` is the property to read rather than comparing this to `currentVersion`. 
     */
    'acceptedVersion'?: string | null;
    /**
     * When the acceptance in force was recorded, in milliseconds; null when nothing has been accepted
     */
    'acceptedAt'?: number | null;
    /**
     * The version Scrivio publishes today
     */
    'currentVersion': string;
    /**
     * True when this organisation has not accepted what Scrivio currently publishes and should be asked to (#4494). A contract signed outside Scrivio is **not** outstanding: nothing can compare a signed PDF to a published version, and treating it as outstanding would prompt every existing customer the moment the backfill lands. 
     */
    'currentVersionOutstanding': boolean;
}


/**
 * Basis GGZ behandelprofiel (Vektis CL0009-NZA). Defined identically in openapi-shared.yaml, openapi-zorgtrajecten.yaml and openapi-zorgtraject-versioned.yaml — every spec generates into nl.scrivio.dto, so the copies must stay structurally identical (openapi:schema-collisions). 
 */

export const BasisGgzProfiel = {
    Kort: 'KORT',
    Middel: 'MIDDEL',
    Intensief: 'INTENSIEF',
    Chronisch: 'CHRONISCH'
} as const;

export type BasisGgzProfiel = typeof BasisGgzProfiel[keyof typeof BasisGgzProfiel];


/**
 * Billing submission method for insurance claims
 */

export const BillingMethod = {
    Vecozo: 'VECOZO',
    Paper: 'PAPER'
} as const;

export type BillingMethod = typeof BillingMethod[keyof typeof BillingMethod];


export interface CreateInvitationRequest {
    /**
     * Email address of the person to invite
     */
    'email': string;
    /**
     * Role to assign to the user when they accept
     */
    'roleId': string;
}
export interface CreateOrganizationRequest {
    /**
     * Organization name. Any character except a control character: the name is interpolated into the subject line of the invitation e-mail, so a CR/LF in it would terminate the header line and turn the rest into headers of the caller\'s choosing (#3457).  The organization\'s URL slug is derived from this name. 
     */
    'name': string;
    /**
     * **Accepted and ignored, and it will be removed** (#4434).  The slug is derived from `name` — lowercased, diacritics folded, non-alphanumerics collapsed to hyphens — and a collision is de-duplicated with a numeric suffix. A practice signing itself up cannot know which slugs are taken, so it is no longer asked to pick one and never sees a failure for it.  The property stays here only so that a frontend build that still sends it keeps working; nothing reads it. It is dropped once nothing sends it. No schema `default:` — a default survives deserialisation and resets the field (#3213). 
     */
    'slug'?: string;
    /**
     * Street address
     */
    'addressStreet'?: string | null;
    /**
     * Postal code
     */
    'addressPostalCode'?: string | null;
    /**
     * City
     */
    'addressCity'?: string | null;
    /**
     * The practice\'s own phone number — **required** here, and only here (#4894). A practice that signs itself up is a customer Scrivio has not spoken to yet, and the number is how it is reached; it is also sent to HubSpot as the company\'s `phone`. `AdminCreateOrganizationRequest` and `UpdateOrganizationRequest` leave it optional, so an organisation from before this, or one the admin portal created, may have none.  A Dutch number, landline or mobile (`0201234567`, `020-123 45 67`, `+31 20 1234567`), stored in its canonical form. A blank or unrecognised number is refused with a `400` naming `phone`. No schema `default:` — a default survives deserialisation (#3213). 
     */
    'phone': string;
    /**
     * Organization email
     */
    'email'?: string | null;
    /**
     * KVK number (Chamber of Commerce)
     */
    'kvkNumber'?: string | null;
    /**
     * AGB code - 8-digit healthcare provider identifier (Algemeen GegevensBeheer)
     */
    'agbCode'?: string | null;
    /**
     * IBAN for client payment invoices
     */
    'iban'?: string | null;
    /**
     * Tenaamstelling: the legal entity a Scrivio invoice to this practice is made out to, when that is not its trading name. Omit it and the invoice falls back to `name`. 
     */
    'statutoryName'?: string | null;
    /**
     * The T.a.v. line on a Scrivio invoice to this practice
     */
    'billingContactName'?: string | null;
    /**
     * Where Scrivio sends this practice its own invoices. Separate from `email`, which is the practice\'s public address. 
     */
    'billingEmail'?: string | null;
    /**
     * BTW-identificatienummer, printed on the Scrivio invoice: `NL`, 9 digits, `B` and 2 digits. Spacing and case are normalised away before it is stored, so `nl 123456789 b01` is accepted and kept as `NL123456789B01`; anything else is a 400. No `pattern:` here for exactly that reason — one would refuse the spacing this normalises. `maxLength` leaves room for that spacing. 
     */
    'btwNumber'?: string | null;
    'defaultLanguage'?: Language;
    /**
     * The agreements the practice accepts by creating this organisation, and the version of each (#4489).  **Every document in `AgreementDocument` must be present at its currently published version.** A missing entry, or one naming any other version, is refused with `400` — the check is server-side, so a disabled checkbox is a courtesy rather than the gate. The two are reported separately: nothing sent means a client that never asked, a different version means a form that was open while a new version was published and has to reload the links and ask again. Either way the refusal carries `code: agreement-acceptance-invalid`, and `GET /api/v1/organizations/agreements` answers what to ask for instead.  Not `required:` in the schema on purpose. The refusal is a business rule with a reason a practice can act on, and a generated bean-validation `400` would say only that a property was absent. It is refused either way.  Not asked here: an organisation Scrivio creates from the admin portal, whose first beheerder accepts when joining it (#4491, `AcceptInvitationRequest`). A version published later is accepted through `POST /api/v1/organizations/me/agreements` (#4494). 
     */
    'acceptedAgreements'?: Array<AcceptedAgreement>;
}


/**
 * Financing regime (wettelijk kader) under which care is funded and declared
 */

export const FinancingRegime = {
    Zvw: 'ZVW',
    Wmo: 'WMO',
    Jeugdwet: 'JEUGDWET',
    Particulier: 'PARTICULIER',
    Pgb: 'PGB',
    Anders: 'ANDERS'
} as const;

export type FinancingRegime = typeof FinancingRegime[keyof typeof FinancingRegime];


export interface InvitationListResponse {
    'items': Array<InvitationResponse>;
}
export interface InvitationResponse {
    /**
     * Unique invitation identifier
     */
    'id': string;
    /**
     * Email address of the invited person
     */
    'email': string;
    /**
     * Name of the role assigned
     */
    'roleName': string;
    /**
     * Current status of the invitation
     */
    'status': InvitationResponseStatusEnum;
    /**
     * Expiration timestamp in milliseconds
     */
    'expiresAt': number;
    /**
     * Full name of the person who sent the invitation
     */
    'invitedByName': string;
    /**
     * Creation timestamp in milliseconds
     */
    'createdAt': number;
}

export const InvitationResponseStatusEnum = {
    Pending: 'PENDING',
    Accepted: 'ACCEPTED',
    Expired: 'EXPIRED',
    Revoked: 'REVOKED'
} as const;

export type InvitationResponseStatusEnum = typeof InvitationResponseStatusEnum[keyof typeof InvitationResponseStatusEnum];

/**
 * Supported language codes for internationalization. A new organization starts on `NL`; that default lives on the `organizations.default_language` column and in `OrganizationService.createOrganization`.  No schema `default:` on purpose (#3213) — a default becomes a pre-initialised request-DTO field, turning a partial update that omits this property into an explicit \"set to NL\". 
 */

export const Language = {
    Nl: 'NL',
    En: 'EN'
} as const;

export type Language = typeof Language[keyof typeof Language];


/**
 * Organization license type determining available features
 */

export const LicenseType = {
    FormsOnly: 'FORMS_ONLY',
    Full: 'FULL'
} as const;

export type LicenseType = typeof LicenseType[keyof typeof LicenseType];


/**
 * The invitations addressed to the authenticated caller.  Items are `PublicInvitationResponse` on purpose rather than a near-copy of it: both answer the same question — what may somebody who has not joined this organization yet be shown about an invitation to it? — so a second schema would be one more place for the token or the invitee\'s address to be added to by mistake.  The `id` each item carries is what `POST /api/v1/users/me/invitations/{invitationId}/accept` takes. 
 */
export interface MyInvitationListResponse {
    'items': Array<PublicInvitationResponse>;
}
/**
 * One checklist step and whether the organization has done it.
 */
export interface OnboardingChecklistItem {
    'key': OnboardingChecklistItemKey;
    /**
     * Derived from what the organization currently has. Never stored, so it can go from true back to false if the underlying data goes away. 
     */
    'completed': boolean;
}


/**
 * Which \"aan de slag\" step an item reports on: - BEHANDELAAR_DESIGNATED: at least one enabled member carries the   `is_behandelaar` designation. A zorgtraject needs a regiebehandelaar,   and the designation — never a role named \"Behandelaar\" — is what the   pickers read. - PRACTICE_AGB_CODE: the organization\'s own AGB code is present and   valid. It is the declarant code on every GDS801 message, so without a   usable one the practice cannot declare at all. - FIRST_CLIENT: the practice has recorded a cliënt. - FIRST_CONTRACT: the practice has a contract that prices something. 
 */

export const OnboardingChecklistItemKey = {
    BehandelaarDesignated: 'BEHANDELAAR_DESIGNATED',
    PracticeAgbCode: 'PRACTICE_AGB_CODE',
    FirstClient: 'FIRST_CLIENT',
    FirstContract: 'FIRST_CONTRACT'
} as const;

export type OnboardingChecklistItemKey = typeof OnboardingChecklistItemKey[keyof typeof OnboardingChecklistItemKey];


/**
 * The \"aan de slag\" checklist for the current organization. Every known item is always present — an absent key means the API no longer has that step, not that it is undone. 
 */
export interface OnboardingChecklistResponse {
    /**
     * All checklist items, in a stable order.
     */
    'items': Array<OnboardingChecklistItem>;
}
/**
 * The identifiers to ask about. Both are optional and either may be sent on its own; an omitted one is not asked about. 
 */
export interface OrganizationIdentifierCheckRequest {
    /**
     * KVK number to ask about. Compared on its digits alone, so separators and spacing do not defeat the check. No length rule: `organizations.kvk_number` carries none, and demanding one here would silently stop the warning firing for any practice whose stored value is shaped differently. 
     */
    'kvkNumber'?: string | null;
    /**
     * AGB code to ask about, in the canonical 8-digit form every other request carrying one uses. The *stored* side is normalised before comparison — `organizations.agb_code` carries a generated `agb_valid` rather than a CHECK (#2033), so it genuinely holds values with separators in them. 
     */
    'agbCode'?: string | null;
}
/**
 * Yes or no, per identifier, and deliberately nothing else — see the operation description. An identifier the request omitted answers `false`. 
 */
export interface OrganizationIdentifierCheckResponse {
    /**
     * An organization already carries this KVK number
     */
    'kvkNumberInUse': boolean;
    /**
     * An organization already carries this AGB code
     */
    'agbCodeInUse': boolean;
}
export interface OrganizationLogoResponse {
    /**
     * URL to the uploaded logo image
     */
    'logoUrl': string;
}
export interface OrganizationResponse {
    /**
     * Unique organization identifier
     */
    'id': string;
    /**
     * Organization name
     */
    'name': string;
    /**
     * URL-friendly slug
     */
    'slug': string;
    /**
     * Street address
     */
    'addressStreet'?: string | null;
    /**
     * Postal code
     */
    'addressPostalCode'?: string | null;
    /**
     * City
     */
    'addressCity'?: string | null;
    /**
     * Organization phone number
     */
    'phone'?: string | null;
    /**
     * Organization email
     */
    'email'?: string | null;
    /**
     * KVK number
     */
    'kvkNumber'?: string | null;
    /**
     * AGB code - 8-digit healthcare provider identifier (existing data may not match the format)
     */
    'agbCode'?: string | null;
    /**
     * IBAN for client payment invoices
     */
    'iban'?: string | null;
    /**
     * Tenaamstelling — the legal entity a Scrivio invoice to this practice is made out to; null when it has given none, and the invoice falls back to name
     */
    'statutoryName'?: string | null;
    /**
     * The T.a.v. line on a Scrivio invoice to this practice; null when it has given none
     */
    'billingContactName'?: string | null;
    /**
     * Where Scrivio sends this practice its own invoices; null when it has given none
     */
    'billingEmail'?: string | null;
    /**
     * BTW-identificatienummer in its canonical NL<9 digits>B<2 digits> form; null when the practice has given none
     */
    'btwNumber'?: string | null;
    /**
     * Where the practice\'s boekhouding receives its verkoopfacturen, one per mail; not billingEmail, which is where Scrivio sends the practice its own invoices; null when it has given none
     */
    'boekhoudingEmail'?: string | null;
    /**
     * Organization settings as JSON object
     */
    'settings'?: { [key: string]: any; };
    /**
     * Creation timestamp in milliseconds
     */
    'createdAt': number;
    /**
     * Last update timestamp in milliseconds
     */
    'updatedAt': number;
    /**
     * URL to organization logo image
     */
    'logoUrl'?: string | null;
    'defaultLanguage': Language;
    'licenseType': LicenseType;
    /**
     * Billing submission method for insurance claims
     */
    'billingMethod'?: BillingMethod | null;
    /**
     * Financing regimes (wettelijke kaders) this organization is enabled to use
     */
    'enabledFinancingRegimes'?: Array<FinancingRegime> | null;
    /**
     * Default treatment setting (kwaliteitsstatuut) pre-selected when creating a new zorgtraject
     */
    'defaultSetting'?: Setting | null;
    /**
     * Default GB-ggz behandelprofiel pre-selected when creating a new zorgtraject; null when the organisation has not configured one
     */
    'defaultBasisGgzProfiel'?: BasisGgzProfiel | null;
    /**
     * Default invoice payment term (betaaltermijn) in days; null when the organisation has not configured one
     */
    'defaultDueDays'?: number | null;
    /**
     * Eigen tarief as a percentage of the NZa maximum, applied to consults no contract prices; null when the organisation has not configured one, which is the full NZa maximum and is not the same as 0
     */
    'ownTariffPercentageOfNza'?: number | null;
    /**
     * How this organization\'s own AGB code is registered in the AGB-register; null when the organisation has not configured one
     */
    'zorgaanbiedersoort'?: Zorgaanbiedersoort | null;
    /**
     * The practice\'s late-cancellation window in hours; 24 unless the organisation set its own
     */
    'lateCancellationNoticeHours': number;
    'vecozoKanalen': VecozoKanalen;
    /**
     * Permission keys available for this organization\'s license type
     */
    'availablePermissions': Array<string>;
    /**
     * Per Scrivio agreement, what this organisation is bound by and whether the currently published version is still outstanding (#4489). One entry per `AgreementDocument` whatever the organisation has recorded, so a reader never has to tell \"no entry\" from \"nothing accepted\". 
     */
    'agreements'?: Array<AgreementStatus>;
}


export interface ProblemDetail {
    /**
     * A URI reference that identifies the problem type
     */
    'type': string;
    /**
     * A short, human-readable summary of the problem
     */
    'title': string;
    /**
     * The HTTP status code
     */
    'status': number;
    /**
     * A human-readable explanation specific to this occurrence
     */
    'detail'?: string;
    /**
     * A URI reference that identifies the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    'field'?: string;
    'message'?: string;
}
/**
 * Public invitation details visible without authentication. Does not include sensitive information.  The `id` is an identifier, not a credential: the only operation that takes it, `POST /api/v1/users/me/invitations/{invitationId}/accept`, looks it up inside the caller\'s own addressed set. The token and the invitee\'s address stay out, which is the rule this schema exists to hold. 
 */
export interface PublicInvitationResponse {
    /**
     * What accepting this invitation has to accept on the practice\'s behalf (#4491), and where each edition can be read. **Empty for nearly every invitation** — it is filled only when the invited role holds `organization.settings.update` *and* the organisation has accepted nothing Scrivio currently publishes: the beheerder of a practice Scrivio created, joining it. A practice that signed itself up, has a signed contract on record or already had a beheerder accept is never asked again.  A screen shows the checkbox when — and only when — this is not empty, and sends each entry\'s `document` and `version` back as `AcceptInvitationRequest.acceptedAgreements`. 
     */
    'agreementsToAccept': Array<PublishedAgreement>;
    /**
     * Id of the invitation
     */
    'id': string;
    /**
     * Name of the organization
     */
    'organizationName': string;
    /**
     * Name of the role assigned
     */
    'roleName': string;
    /**
     * Full name of the person who sent the invitation
     */
    'invitedByName': string;
    /**
     * Expiration timestamp in milliseconds
     */
    'expiresAt': number;
}
/**
 * One Scrivio agreement as currently published, for a practice to read before it accepts it — signing itself up (#4490), joining by invitation as its beheerder (#4491), or asked to accept a newer version (#4494). 
 */
export interface PublishedAgreement {
    'document': AgreementDocument;
    /**
     * The version to send back as `AcceptedAgreement.version`
     */
    'version': string;
    /**
     * The page this version is published on. A later edition gets a page of its own.
     */
    'url': string;
    /**
     * What this version changed, in one line, so a practice asked to accept it is told what is different rather than only that something is (#4494). Published with the version and written in Dutch for the practice, like the document itself. 
     */
    'summary': string;
}


/**
 * Treatment setting (behandelsetting) per NZA tariff structure: - AMBULANT_MONO: Ambulant monodisciplinair (outpatient monodisciplinary) - AMBULANT_MULTI: Ambulant multidisciplinair (outpatient multidisciplinary) - KLINISCH: Klinisch (inpatient clinical) - PRAKTIJK: Praktijk (private practice) 
 */

export const Setting = {
    AmbulantMono: 'AMBULANT_MONO',
    AmbulantMulti: 'AMBULANT_MULTI',
    Klinisch: 'KLINISCH',
    Praktijk: 'PRAKTIJK'
} as const;

export type Setting = typeof Setting[keyof typeof Setting];


export interface UpdateOrganizationRequest {
    /**
     * Organization name. Any character except a control character: the name is interpolated into the subject line of the invitation e-mail, so a CR/LF in it would terminate the header line and turn the rest into headers of the caller\'s choosing (#3457). 
     */
    'name'?: string;
    /**
     * Street address. Send null to clear it; omit to leave unchanged.
     */
    'addressStreet'?: string | null;
    /**
     * Postal code. Send null to clear it; omit to leave unchanged.
     */
    'addressPostalCode'?: string | null;
    /**
     * City. Send null to clear it; omit to leave unchanged.
     */
    'addressCity'?: string | null;
    /**
     * Organization phone number. Send null to clear it; omit to leave unchanged.
     */
    'phone'?: string | null;
    /**
     * Organization email. Send null to clear it; omit to leave unchanged.
     */
    'email'?: string | null;
    /**
     * KVK number (Chamber of Commerce). Send null to clear it; omit to leave unchanged.
     */
    'kvkNumber'?: string | null;
    /**
     * AGB code - 8-digit healthcare provider identifier (Algemeen GegevensBeheer). Send null to clear it; omit to leave unchanged.
     */
    'agbCode'?: string | null;
    /**
     * IBAN for client payment invoices. Send null to clear it; omit to leave unchanged.
     */
    'iban'?: string | null;
    /**
     * Tenaamstelling: the legal entity a Scrivio invoice to this practice is made out to. Send null to clear it, which falls the invoice back to `name`. Omit to leave unchanged. 
     */
    'statutoryName'?: string | null;
    /**
     * The T.a.v. line on a Scrivio invoice to this practice. Send null to clear it — which is what a practice does when the colleague it names has left. Omit to leave unchanged. 
     */
    'billingContactName'?: string | null;
    /**
     * Where Scrivio sends this practice its own invoices. Separate from `email`, which is the practice\'s public address. Send null to clear it. Omit to leave unchanged. 
     */
    'billingEmail'?: string | null;
    /**
     * BTW-identificatienummer, printed on the Scrivio invoice: `NL`, 9 digits, `B` and 2 digits. Spacing and case are normalised away before it is stored, so `nl 123456789 b01` is accepted and kept as `NL123456789B01`; anything else is a 400. No `pattern:` here for exactly that reason — one would refuse the spacing this normalises. Send null to clear it. Omit to leave unchanged. 
     */
    'btwNumber'?: string | null;
    /**
     * Where the practice\'s boekhouding receives its verkoopfacturen, one per mail — a SnelStart inlees-mailbox or a bookkeeper\'s inbox. Not `billingEmail`, which is where Scrivio sends the practice its own invoices. One address. Send null (or an empty string) to clear it. Omit to leave unchanged. 
     */
    'boekhoudingEmail'?: string | null;
    'defaultLanguage'?: Language;
    /**
     * Billing submission method for insurance claims
     */
    'billingMethod'?: BillingMethod | null;
    /**
     * Financing regimes (wettelijke kaders) this organization is enabled to use
     */
    'enabledFinancingRegimes'?: Array<FinancingRegime> | null;
    /**
     * Default treatment setting (kwaliteitsstatuut) pre-selected when creating a new zorgtraject. Send null to clear the default. Omit to leave unchanged. 
     */
    'defaultSetting'?: Setting | null;
    /**
     * Default GB-ggz behandelprofiel pre-selected when creating a new zorgtraject, along with ggzType BASIS_GGZ (a profiel implies basis ggz). Send null to clear the default. Omit to leave unchanged. 
     */
    'defaultBasisGgzProfiel'?: BasisGgzProfiel | null;
    /**
     * Default invoice payment term (betaaltermijn) in days. A new invoice created without an explicit dueDays uses this value; when unset the last-resort default of 30 days applies. 0 means \"betaal direct\" — due on the invoice date itself. Send null to clear the default. Omit to leave unchanged. 
     */
    'defaultDueDays'?: number | null;
    /**
     * Eigen tarief: what this practice charges for a consult no contract prices, as a percentage of the NZa maximum. Applied below every contract rung and above the 100% fallback, so a contracted line is unaffected. Send null to clear it — which is \"no eigen tarief\", the full NZa maximum, and is not the same as 0. Omit to leave unchanged. The upper bound is 100 rather than a contract rule\'s 200: that is a reimbursement percentage, not a charge. 
     */
    'ownTariffPercentageOfNza'?: number | null;
    /**
     * How this organization\'s own AGB code is registered in the AGB-register. Send null to clear it. Omit to leave unchanged. 
     */
    'zorgaanbiedersoort'?: Zorgaanbiedersoort | null;
    /**
     * The practice\'s late-cancellation window (afmeldbeleid), in hours. An afspraak cancelled fewer than this many hours before its start is classified as CANCELLED_LATE; this many or more ahead as CANCELLED_ADVANCE. Applies to cancellations made from now on — an afspraak already cancelled keeps its status. Omit to leave unchanged. These bounds are the only declaration of the range: the backend and the frontend carry no copy of them. 
     */
    'lateCancellationNoticeHours'?: number;
}


/**
 * How one of an organization\'s VECOZO streams travels (ADR-049 §2): `PORTAAL` is the practice\'s own export/import in VECOZO\'s portal, `BERICHTENSERVICE` is Scrivio sending and pulling the messages itself. Not the VECOZO environment, which is the deployment\'s. 
 */

export const VecozoKanaal = {
    Portaal: 'PORTAAL',
    Berichtenservice: 'BERICHTENSERVICE'
} as const;

export type VecozoKanaal = typeof VecozoKanaal[keyof typeof VecozoKanaal];


/**
 * How each of the organisation\'s VECOZO stream families effectively travels on this deployment (ADR-049 §2): what Scrivio staff set in the admin portal, except that a stream reads `PORTAAL` wherever the deployment has no berichtenservice connection. Read-only — only Scrivio staff change it, through the admin portal. 
 */
export interface VecozoKanalen {
    'gds801': VecozoKanaal;
    'ijw': VecozoKanaal;
}


/**
 * Kind of declaring healthcare provider (Vektis CL0015-VEKT), written to GDS801 rubriek 3802 `DeclaratieContext/Declarant/ZorgaanbiederSoort`: - INSTELLING: 1 - instelling (institution) - PRAKTIJK: 2 - praktijkcode (practice) - ZORGVERLENER: 3 - zorgverlener (individual practitioner)  This must match how the organization\'s own AGB code is registered in the AGB-register: an insurer validates the pair and rejects the whole message when they disagree — which is what a declarant hardcoded to instelling did to the first production declaration of a praktijk (#3948). 
 */

export const Zorgaanbiedersoort = {
    Instelling: 'INSTELLING',
    Praktijk: 'PRAKTIJK',
    Zorgverlener: 'ZORGVERLENER'
} as const;

export type Zorgaanbiedersoort = typeof Zorgaanbiedersoort[keyof typeof Zorgaanbiedersoort];



/**
 * InvitationsApi - axios parameter creator
 */
export const InvitationsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Accept an invitation and join the organization. The authenticated user will be added to the organization with the invited role.  The invitation must have been sent to the authenticated user\'s own email address. Holding the link is not enough: accepting an invitation addressed to somebody else is refused with 403 and leaves the invitation pending, so its real recipient can still use it. The addresses are compared case-insensitively and exactly otherwise — plus-addressing is not stripped, so `name+practice@example.nl` is a different recipient from `name@example.nl`.  The invited organization becomes the user\'s active organization only when they had none; accepting an invitation never switches the organization an existing user is working in.  **A beheerder joining a practice that has accepted nothing accepts for it** (#4491). When the invitation\'s `agreementsToAccept` is not empty, the body has to name the current version of each of those documents, or the request is refused with 400 and the invitation stays pending. Otherwise the body is optional and ignored. 
         * @summary Accept invitation
         * @param {string} token The invitation token from the email link
         * @param {AcceptInvitationRequest} [acceptInvitationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        acceptInvitation: async (token: string, acceptInvitationRequest?: AcceptInvitationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'token' is not null or undefined
            assertParamExists('acceptInvitation', 'token', token)
            const localVarPath = `/api/v1/invitations/{token}/accept`
                .replace(`{${"token"}}`, encodeURIComponent(String(token)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(acceptInvitationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Accept an invitation listed by `GET /api/v1/users/me/invitations`, for a caller who reached it from inside the application rather than from the e-mail link.  It exists because the listing carries no token: without it, somebody whose invitation e-mail was lost or filtered could see that they had been invited and still have no way to act on it — and creating their own organization, the only other move the dashboard offers, leaves them with a second membership and an empty practice they cannot remove.  The invitation is resolved **inside the caller\'s own set** — the same pending, unexpired, addressed-to-me query the listing answers from — and then redeemed through the one acceptance path, which re-checks the address itself. An id belonging to somebody else\'s invitation, or to one that has since expired, been revoked or been used, is therefore simply not found: possession of an id grants nothing.  The invited organization becomes the user\'s active organization only when they had none; accepting an invitation never switches the organization an existing user is working in.  When the listed invitation\'s `agreementsToAccept` is not empty, the body has to accept each of those editions — the same rule as `POST /api/v1/invitations/{token}/accept` (#4491). 
         * @summary Accept one of the invitations addressed to me
         * @param {string} invitationId Id of an invitation from &#x60;GET /api/v1/users/me/invitations&#x60;
         * @param {AcceptInvitationRequest} [acceptInvitationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        acceptMyInvitation: async (invitationId: string, acceptInvitationRequest?: AcceptInvitationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invitationId' is not null or undefined
            assertParamExists('acceptMyInvitation', 'invitationId', invitationId)
            const localVarPath = `/api/v1/users/me/invitations/{invitationId}/accept`
                .replace(`{${"invitationId"}}`, encodeURIComponent(String(invitationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(acceptInvitationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Send an invitation to a user to join the current organization. Requires `users.invite` permission. 
         * @summary Create invitation
         * @param {CreateInvitationRequest} createInvitationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createInvitation: async (createInvitationRequest: CreateInvitationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createInvitationRequest' is not null or undefined
            assertParamExists('createInvitation', 'createInvitationRequest', createInvitationRequest)
            const localVarPath = `/api/v1/organizations/invitations`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createInvitationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Get public details about an invitation using its token. This endpoint is public (no authentication required). Used to show invitation details before user signs in. 
         * @summary Get invitation details (public)
         * @param {string} token The invitation token from the email link
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getInvitationByToken: async (token: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'token' is not null or undefined
            assertParamExists('getInvitationByToken', 'token', token)
            const localVarPath = `/api/v1/invitations/{token}`
                .replace(`{${"token"}}`, encodeURIComponent(String(token)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List all pending invitations for the current organization. Requires `users.invite` permission. 
         * @summary List pending invitations
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listInvitations: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/organizations/invitations`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The pending, unexpired invitations addressed to the authenticated user\'s own email address, across every organization that has invited them.  Authentication is the only requirement, deliberately: the caller this exists for is someone who has been invited and belongs to no organization yet, and so holds no permissions at all. Without it their only way of discovering an invitation is the e-mail — and if that is lost or filtered, creating their own organization is the only move left, which makes the invitation permanently unacceptable.  The address is taken from the authenticated principal. There is no parameter for it, so there is no way to ask about anybody else\'s invitations.  No token is returned, so this response cannot be replayed as an acceptance by whoever happens to hold it. Each item does carry its `id`, which `POST /api/v1/users/me/invitations/{invitationId}/accept` takes — that path resolves the invitation inside the caller\'s own addressed set, so the id is an identifier rather than a credential.  A user who already belongs to an organization sees their outstanding invitations here too — this reports, it does not judge whether they can be accepted. 
         * @summary List the invitations addressed to me
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyInvitations: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/users/me/invitations`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Revoke a pending invitation. Requires `users.invite` permission. 
         * @summary Revoke invitation
         * @param {string} invitationId The invitation ID to revoke
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revokeInvitation: async (invitationId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invitationId' is not null or undefined
            assertParamExists('revokeInvitation', 'invitationId', invitationId)
            const localVarPath = `/api/v1/organizations/invitations/{invitationId}`
                .replace(`{${"invitationId"}}`, encodeURIComponent(String(invitationId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * InvitationsApi - functional programming interface
 */
export const InvitationsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = InvitationsApiAxiosParamCreator(configuration)
    return {
        /**
         * Accept an invitation and join the organization. The authenticated user will be added to the organization with the invited role.  The invitation must have been sent to the authenticated user\'s own email address. Holding the link is not enough: accepting an invitation addressed to somebody else is refused with 403 and leaves the invitation pending, so its real recipient can still use it. The addresses are compared case-insensitively and exactly otherwise — plus-addressing is not stripped, so `name+practice@example.nl` is a different recipient from `name@example.nl`.  The invited organization becomes the user\'s active organization only when they had none; accepting an invitation never switches the organization an existing user is working in.  **A beheerder joining a practice that has accepted nothing accepts for it** (#4491). When the invitation\'s `agreementsToAccept` is not empty, the body has to name the current version of each of those documents, or the request is refused with 400 and the invitation stays pending. Otherwise the body is optional and ignored. 
         * @summary Accept invitation
         * @param {string} token The invitation token from the email link
         * @param {AcceptInvitationRequest} [acceptInvitationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async acceptInvitation(token: string, acceptInvitationRequest?: AcceptInvitationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AcceptInvitationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.acceptInvitation(token, acceptInvitationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvitationsApi.acceptInvitation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Accept an invitation listed by `GET /api/v1/users/me/invitations`, for a caller who reached it from inside the application rather than from the e-mail link.  It exists because the listing carries no token: without it, somebody whose invitation e-mail was lost or filtered could see that they had been invited and still have no way to act on it — and creating their own organization, the only other move the dashboard offers, leaves them with a second membership and an empty practice they cannot remove.  The invitation is resolved **inside the caller\'s own set** — the same pending, unexpired, addressed-to-me query the listing answers from — and then redeemed through the one acceptance path, which re-checks the address itself. An id belonging to somebody else\'s invitation, or to one that has since expired, been revoked or been used, is therefore simply not found: possession of an id grants nothing.  The invited organization becomes the user\'s active organization only when they had none; accepting an invitation never switches the organization an existing user is working in.  When the listed invitation\'s `agreementsToAccept` is not empty, the body has to accept each of those editions — the same rule as `POST /api/v1/invitations/{token}/accept` (#4491). 
         * @summary Accept one of the invitations addressed to me
         * @param {string} invitationId Id of an invitation from &#x60;GET /api/v1/users/me/invitations&#x60;
         * @param {AcceptInvitationRequest} [acceptInvitationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async acceptMyInvitation(invitationId: string, acceptInvitationRequest?: AcceptInvitationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AcceptInvitationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.acceptMyInvitation(invitationId, acceptInvitationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvitationsApi.acceptMyInvitation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Send an invitation to a user to join the current organization. Requires `users.invite` permission. 
         * @summary Create invitation
         * @param {CreateInvitationRequest} createInvitationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createInvitation(createInvitationRequest: CreateInvitationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<InvitationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createInvitation(createInvitationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvitationsApi.createInvitation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Get public details about an invitation using its token. This endpoint is public (no authentication required). Used to show invitation details before user signs in. 
         * @summary Get invitation details (public)
         * @param {string} token The invitation token from the email link
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getInvitationByToken(token: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<PublicInvitationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getInvitationByToken(token, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvitationsApi.getInvitationByToken']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * List all pending invitations for the current organization. Requires `users.invite` permission. 
         * @summary List pending invitations
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listInvitations(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<InvitationListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listInvitations(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvitationsApi.listInvitations']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The pending, unexpired invitations addressed to the authenticated user\'s own email address, across every organization that has invited them.  Authentication is the only requirement, deliberately: the caller this exists for is someone who has been invited and belongs to no organization yet, and so holds no permissions at all. Without it their only way of discovering an invitation is the e-mail — and if that is lost or filtered, creating their own organization is the only move left, which makes the invitation permanently unacceptable.  The address is taken from the authenticated principal. There is no parameter for it, so there is no way to ask about anybody else\'s invitations.  No token is returned, so this response cannot be replayed as an acceptance by whoever happens to hold it. Each item does carry its `id`, which `POST /api/v1/users/me/invitations/{invitationId}/accept` takes — that path resolves the invitation inside the caller\'s own addressed set, so the id is an identifier rather than a credential.  A user who already belongs to an organization sees their outstanding invitations here too — this reports, it does not judge whether they can be accepted. 
         * @summary List the invitations addressed to me
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listMyInvitations(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MyInvitationListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listMyInvitations(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvitationsApi.listMyInvitations']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Revoke a pending invitation. Requires `users.invite` permission. 
         * @summary Revoke invitation
         * @param {string} invitationId The invitation ID to revoke
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async revokeInvitation(invitationId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.revokeInvitation(invitationId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvitationsApi.revokeInvitation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * InvitationsApi - factory interface
 */
export const InvitationsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = InvitationsApiFp(configuration)
    return {
        /**
         * Accept an invitation and join the organization. The authenticated user will be added to the organization with the invited role.  The invitation must have been sent to the authenticated user\'s own email address. Holding the link is not enough: accepting an invitation addressed to somebody else is refused with 403 and leaves the invitation pending, so its real recipient can still use it. The addresses are compared case-insensitively and exactly otherwise — plus-addressing is not stripped, so `name+practice@example.nl` is a different recipient from `name@example.nl`.  The invited organization becomes the user\'s active organization only when they had none; accepting an invitation never switches the organization an existing user is working in.  **A beheerder joining a practice that has accepted nothing accepts for it** (#4491). When the invitation\'s `agreementsToAccept` is not empty, the body has to name the current version of each of those documents, or the request is refused with 400 and the invitation stays pending. Otherwise the body is optional and ignored. 
         * @summary Accept invitation
         * @param {string} token The invitation token from the email link
         * @param {AcceptInvitationRequest} [acceptInvitationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        acceptInvitation(token: string, acceptInvitationRequest?: AcceptInvitationRequest, options?: RawAxiosRequestConfig): AxiosPromise<AcceptInvitationResponse> {
            return localVarFp.acceptInvitation(token, acceptInvitationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Accept an invitation listed by `GET /api/v1/users/me/invitations`, for a caller who reached it from inside the application rather than from the e-mail link.  It exists because the listing carries no token: without it, somebody whose invitation e-mail was lost or filtered could see that they had been invited and still have no way to act on it — and creating their own organization, the only other move the dashboard offers, leaves them with a second membership and an empty practice they cannot remove.  The invitation is resolved **inside the caller\'s own set** — the same pending, unexpired, addressed-to-me query the listing answers from — and then redeemed through the one acceptance path, which re-checks the address itself. An id belonging to somebody else\'s invitation, or to one that has since expired, been revoked or been used, is therefore simply not found: possession of an id grants nothing.  The invited organization becomes the user\'s active organization only when they had none; accepting an invitation never switches the organization an existing user is working in.  When the listed invitation\'s `agreementsToAccept` is not empty, the body has to accept each of those editions — the same rule as `POST /api/v1/invitations/{token}/accept` (#4491). 
         * @summary Accept one of the invitations addressed to me
         * @param {string} invitationId Id of an invitation from &#x60;GET /api/v1/users/me/invitations&#x60;
         * @param {AcceptInvitationRequest} [acceptInvitationRequest] 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        acceptMyInvitation(invitationId: string, acceptInvitationRequest?: AcceptInvitationRequest, options?: RawAxiosRequestConfig): AxiosPromise<AcceptInvitationResponse> {
            return localVarFp.acceptMyInvitation(invitationId, acceptInvitationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Send an invitation to a user to join the current organization. Requires `users.invite` permission. 
         * @summary Create invitation
         * @param {CreateInvitationRequest} createInvitationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createInvitation(createInvitationRequest: CreateInvitationRequest, options?: RawAxiosRequestConfig): AxiosPromise<InvitationResponse> {
            return localVarFp.createInvitation(createInvitationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Get public details about an invitation using its token. This endpoint is public (no authentication required). Used to show invitation details before user signs in. 
         * @summary Get invitation details (public)
         * @param {string} token The invitation token from the email link
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getInvitationByToken(token: string, options?: RawAxiosRequestConfig): AxiosPromise<PublicInvitationResponse> {
            return localVarFp.getInvitationByToken(token, options).then((request) => request(axios, basePath));
        },
        /**
         * List all pending invitations for the current organization. Requires `users.invite` permission. 
         * @summary List pending invitations
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listInvitations(options?: RawAxiosRequestConfig): AxiosPromise<InvitationListResponse> {
            return localVarFp.listInvitations(options).then((request) => request(axios, basePath));
        },
        /**
         * The pending, unexpired invitations addressed to the authenticated user\'s own email address, across every organization that has invited them.  Authentication is the only requirement, deliberately: the caller this exists for is someone who has been invited and belongs to no organization yet, and so holds no permissions at all. Without it their only way of discovering an invitation is the e-mail — and if that is lost or filtered, creating their own organization is the only move left, which makes the invitation permanently unacceptable.  The address is taken from the authenticated principal. There is no parameter for it, so there is no way to ask about anybody else\'s invitations.  No token is returned, so this response cannot be replayed as an acceptance by whoever happens to hold it. Each item does carry its `id`, which `POST /api/v1/users/me/invitations/{invitationId}/accept` takes — that path resolves the invitation inside the caller\'s own addressed set, so the id is an identifier rather than a credential.  A user who already belongs to an organization sees their outstanding invitations here too — this reports, it does not judge whether they can be accepted. 
         * @summary List the invitations addressed to me
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listMyInvitations(options?: RawAxiosRequestConfig): AxiosPromise<MyInvitationListResponse> {
            return localVarFp.listMyInvitations(options).then((request) => request(axios, basePath));
        },
        /**
         * Revoke a pending invitation. Requires `users.invite` permission. 
         * @summary Revoke invitation
         * @param {string} invitationId The invitation ID to revoke
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        revokeInvitation(invitationId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.revokeInvitation(invitationId, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * InvitationsApi - object-oriented interface
 */
export class InvitationsApi extends BaseAPI {
    /**
     * Accept an invitation and join the organization. The authenticated user will be added to the organization with the invited role.  The invitation must have been sent to the authenticated user\'s own email address. Holding the link is not enough: accepting an invitation addressed to somebody else is refused with 403 and leaves the invitation pending, so its real recipient can still use it. The addresses are compared case-insensitively and exactly otherwise — plus-addressing is not stripped, so `name+practice@example.nl` is a different recipient from `name@example.nl`.  The invited organization becomes the user\'s active organization only when they had none; accepting an invitation never switches the organization an existing user is working in.  **A beheerder joining a practice that has accepted nothing accepts for it** (#4491). When the invitation\'s `agreementsToAccept` is not empty, the body has to name the current version of each of those documents, or the request is refused with 400 and the invitation stays pending. Otherwise the body is optional and ignored. 
     * @summary Accept invitation
     * @param {string} token The invitation token from the email link
     * @param {AcceptInvitationRequest} [acceptInvitationRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public acceptInvitation(token: string, acceptInvitationRequest?: AcceptInvitationRequest, options?: RawAxiosRequestConfig) {
        return InvitationsApiFp(this.configuration).acceptInvitation(token, acceptInvitationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Accept an invitation listed by `GET /api/v1/users/me/invitations`, for a caller who reached it from inside the application rather than from the e-mail link.  It exists because the listing carries no token: without it, somebody whose invitation e-mail was lost or filtered could see that they had been invited and still have no way to act on it — and creating their own organization, the only other move the dashboard offers, leaves them with a second membership and an empty practice they cannot remove.  The invitation is resolved **inside the caller\'s own set** — the same pending, unexpired, addressed-to-me query the listing answers from — and then redeemed through the one acceptance path, which re-checks the address itself. An id belonging to somebody else\'s invitation, or to one that has since expired, been revoked or been used, is therefore simply not found: possession of an id grants nothing.  The invited organization becomes the user\'s active organization only when they had none; accepting an invitation never switches the organization an existing user is working in.  When the listed invitation\'s `agreementsToAccept` is not empty, the body has to accept each of those editions — the same rule as `POST /api/v1/invitations/{token}/accept` (#4491). 
     * @summary Accept one of the invitations addressed to me
     * @param {string} invitationId Id of an invitation from &#x60;GET /api/v1/users/me/invitations&#x60;
     * @param {AcceptInvitationRequest} [acceptInvitationRequest] 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public acceptMyInvitation(invitationId: string, acceptInvitationRequest?: AcceptInvitationRequest, options?: RawAxiosRequestConfig) {
        return InvitationsApiFp(this.configuration).acceptMyInvitation(invitationId, acceptInvitationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Send an invitation to a user to join the current organization. Requires `users.invite` permission. 
     * @summary Create invitation
     * @param {CreateInvitationRequest} createInvitationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createInvitation(createInvitationRequest: CreateInvitationRequest, options?: RawAxiosRequestConfig) {
        return InvitationsApiFp(this.configuration).createInvitation(createInvitationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Get public details about an invitation using its token. This endpoint is public (no authentication required). Used to show invitation details before user signs in. 
     * @summary Get invitation details (public)
     * @param {string} token The invitation token from the email link
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getInvitationByToken(token: string, options?: RawAxiosRequestConfig) {
        return InvitationsApiFp(this.configuration).getInvitationByToken(token, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List all pending invitations for the current organization. Requires `users.invite` permission. 
     * @summary List pending invitations
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listInvitations(options?: RawAxiosRequestConfig) {
        return InvitationsApiFp(this.configuration).listInvitations(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The pending, unexpired invitations addressed to the authenticated user\'s own email address, across every organization that has invited them.  Authentication is the only requirement, deliberately: the caller this exists for is someone who has been invited and belongs to no organization yet, and so holds no permissions at all. Without it their only way of discovering an invitation is the e-mail — and if that is lost or filtered, creating their own organization is the only move left, which makes the invitation permanently unacceptable.  The address is taken from the authenticated principal. There is no parameter for it, so there is no way to ask about anybody else\'s invitations.  No token is returned, so this response cannot be replayed as an acceptance by whoever happens to hold it. Each item does carry its `id`, which `POST /api/v1/users/me/invitations/{invitationId}/accept` takes — that path resolves the invitation inside the caller\'s own addressed set, so the id is an identifier rather than a credential.  A user who already belongs to an organization sees their outstanding invitations here too — this reports, it does not judge whether they can be accepted. 
     * @summary List the invitations addressed to me
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listMyInvitations(options?: RawAxiosRequestConfig) {
        return InvitationsApiFp(this.configuration).listMyInvitations(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Revoke a pending invitation. Requires `users.invite` permission. 
     * @summary Revoke invitation
     * @param {string} invitationId The invitation ID to revoke
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public revokeInvitation(invitationId: string, options?: RawAxiosRequestConfig) {
        return InvitationsApiFp(this.configuration).revokeInvitation(invitationId, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * OrganizationsApi - axios parameter creator
 */
export const OrganizationsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Records that the organisation accepts the currently published version of each agreement it is not yet bound by (#4494) — the *Akkoord* of the prompt a user holding `organization.settings.update` is shown after sign-in while `OrganizationResponse.agreements` reports a `currentVersionOutstanding`.  The body names what the prompt showed. **Every outstanding document must be present at its current version, and no document may name another version**; otherwise `400` with `code: agreement-acceptance-invalid` and nothing is recorded — a prompt left open while a new version was published has to reload and ask again.  A document that is not outstanding is not recorded again, so a second beheerder answering the same prompt records nothing and still gets `200`. Each document that is recorded is a new acceptance: `REACCEPTANCE` where the organisation had accepted an earlier version, `SELF_SIGNUP` where it had accepted nothing. The party details are the organisation\'s as they stand.  Nothing is gated on this: an organisation that never answers keeps working under the version it accepted. 
         * @summary Accept the current version of every outstanding agreement
         * @param {AcceptAgreementsRequest} acceptAgreementsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        acceptAgreements: async (acceptAgreementsRequest: AcceptAgreementsRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'acceptAgreementsRequest' is not null or undefined
            assertParamExists('acceptAgreements', 'acceptAgreementsRequest', acceptAgreementsRequest)
            const localVarPath = `/api/v1/organizations/me/agreements`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(acceptAgreementsRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Answers whether an organisation in Scrivio already carries the given KvK-nummer or AGB-code, so the create-organisation form can warn a practice that appears to be here already: a colleague signs up from the website not knowing a beheerder was invited, and the practice ends up with two organisations splitting its cliënten, declaraties and contracten (#4438).  **The answer is yes or no and nothing else.** No name, no id, no contact. The caller typically has no organisation of their own, so anything more would turn this into a directory of the practices on the platform. It is bounded further by a per-user rate limit, and the 429 says nothing about whether anything matched.  It is a **warning, not a gate**. `POST /api/v1/organizations` does not consult this and refuses nothing on account of it: a second organisation under one KvK is legitimate — a practice running two legal entities, or a deliberately separate environment — which is why neither column carries a uniqueness constraint and neither should.  Comparison is on the normalised value, so a stored `03-012345` answers for a requested `03012345`. An omitted value is not asked about and its answer is `false`.  A POST rather than a GET because a query parameter travels in the request line, which every access log along the way records (ADR-016). 
         * @summary Check whether a KvK-nummer or AGB-code is already in use
         * @param {OrganizationIdentifierCheckRequest} organizationIdentifierCheckRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        checkOrganizationIdentifiers: async (organizationIdentifierCheckRequest: OrganizationIdentifierCheckRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'organizationIdentifierCheckRequest' is not null or undefined
            assertParamExists('checkOrganizationIdentifiers', 'organizationIdentifierCheckRequest', organizationIdentifierCheckRequest)
            const localVarPath = `/api/v1/organizations/identifier-check`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(organizationIdentifierCheckRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Creates a new organization and assigns the current user as administrator. This is used for organization onboarding when a user doesn\'t have an organization yet. 
         * @summary Create organization
         * @param {CreateOrganizationRequest} createOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createOrganization: async (createOrganizationRequest: CreateOrganizationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createOrganizationRequest' is not null or undefined
            assertParamExists('createOrganization', 'createOrganizationRequest', createOrganizationRequest)
            const localVarPath = `/api/v1/organizations`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createOrganizationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Removes the current organization\'s logo. Requires `organization.settings.update` permission. 
         * @summary Delete organization logo
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteOrganizationLogo: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/organizations/me/logo`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a stable `{ behandelaarId: paletteIndex }` map for the current user\'s organization. Palette indices are assigned lazily and persisted server-side, so a behandelaar keeps the same colour across sessions, users, and member (de)selection. Any behandelaar without an index yet is assigned the lowest free palette index (0-based) and the map is persisted before it is returned.  The colour palette itself lives in the frontend; the backend only stores and serves the integer index.  Org-scoped: any authenticated organization member may read. 
         * @summary Get per-behandelaar agenda colours
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAgendaColors: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/organizations/me/agenda-colors`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the organization of the currently authenticated user
         * @summary Get current organization
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCurrentOrganization: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/organizations/me`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the four things a newly created practice still has to do before Scrivio is useful, each with a derived completion flag.  Completion is **derived on every request** and never stored: there is no acknowledgement state, so an item cannot disagree with what the organization actually has, and it ticks itself as the practice works.  Items seeded by organization creation — afspraaktypes, briefsjablonen, the default locatie — are deliberately absent: they are green before the practice has logged in and are therefore not tasks. So are cliëntportaal and afspraakherinneringen, which are always on, and declaratiestromen (`enabledFinancingRegimes`): Zvw is on without asking, and only a practice that also works under the Jeugdwet has anything to switch on.  Org-scoped: any authenticated organization member may read. It discloses nothing a member cannot already see, so it needs no permission. 
         * @summary Get the \"aan de slag\" checklist for the current organization
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOnboardingChecklist: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/organizations/me/onboarding-checklist`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieves the current organization\'s logo image. Streams the image bytes with the correct Content-Type header. 
         * @summary Get organization logo
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrganizationLogo: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/organizations/me/logo`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'image/png,image/jpeg,image/svg+xml,application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Each Scrivio agreement at the version currently published, and the page that version is published on (#4490). The create-organisation form links these and sends the versions back in `CreateOrganizationRequest.acceptedAgreements`, so neither a version nor a link is ever typed into a client.  Open to any authenticated user: the caller is a practice about to sign itself up, which has no organisation for `OrganizationResponse.agreements` to answer about.  The link is derived from the version — each edition has a page of its own that a later one never overwrites — so the text a practice reads is the text its acceptance names. 
         * @summary The agreements a practice accepts by creating an organisation
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getPublishedAgreements: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/organizations/agreements`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates the organization of the currently authenticated user. Requires `organization.settings.update` permission. 
         * @summary Update current organization
         * @param {UpdateOrganizationRequest} updateOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateCurrentOrganization: async (updateOrganizationRequest: UpdateOrganizationRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'updateOrganizationRequest' is not null or undefined
            assertParamExists('updateCurrentOrganization', 'updateOrganizationRequest', updateOrganizationRequest)
            const localVarPath = `/api/v1/organizations/me`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateOrganizationRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Uploads a new logo for the current organization. Accepts PNG, JPEG, or SVG images up to 2MB. Recommended dimensions: 300x100 pixels. Requires `organization.settings.update` permission. 
         * @summary Upload organization logo
         * @param {File} file Logo image file (PNG, JPEG, or SVG, max 2MB)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadOrganizationLogo: async (file: File, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'file' is not null or undefined
            assertParamExists('uploadOrganizationLogo', 'file', file)
            const localVarPath = `/api/v1/organizations/me/logo`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;
            const localVarFormParams = new ((configuration && configuration.formDataCtor) || FormData)();

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)


            if (file !== undefined) { 
                localVarFormParams.append('file', file as any);
            }
            localVarHeaderParameter['Content-Type'] = 'multipart/form-data';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = localVarFormParams;

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * OrganizationsApi - functional programming interface
 */
export const OrganizationsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = OrganizationsApiAxiosParamCreator(configuration)
    return {
        /**
         * Records that the organisation accepts the currently published version of each agreement it is not yet bound by (#4494) — the *Akkoord* of the prompt a user holding `organization.settings.update` is shown after sign-in while `OrganizationResponse.agreements` reports a `currentVersionOutstanding`.  The body names what the prompt showed. **Every outstanding document must be present at its current version, and no document may name another version**; otherwise `400` with `code: agreement-acceptance-invalid` and nothing is recorded — a prompt left open while a new version was published has to reload and ask again.  A document that is not outstanding is not recorded again, so a second beheerder answering the same prompt records nothing and still gets `200`. Each document that is recorded is a new acceptance: `REACCEPTANCE` where the organisation had accepted an earlier version, `SELF_SIGNUP` where it had accepted nothing. The party details are the organisation\'s as they stand.  Nothing is gated on this: an organisation that never answers keeps working under the version it accepted. 
         * @summary Accept the current version of every outstanding agreement
         * @param {AcceptAgreementsRequest} acceptAgreementsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async acceptAgreements(acceptAgreementsRequest: AcceptAgreementsRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<AgreementStatus>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.acceptAgreements(acceptAgreementsRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationsApi.acceptAgreements']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Answers whether an organisation in Scrivio already carries the given KvK-nummer or AGB-code, so the create-organisation form can warn a practice that appears to be here already: a colleague signs up from the website not knowing a beheerder was invited, and the practice ends up with two organisations splitting its cliënten, declaraties and contracten (#4438).  **The answer is yes or no and nothing else.** No name, no id, no contact. The caller typically has no organisation of their own, so anything more would turn this into a directory of the practices on the platform. It is bounded further by a per-user rate limit, and the 429 says nothing about whether anything matched.  It is a **warning, not a gate**. `POST /api/v1/organizations` does not consult this and refuses nothing on account of it: a second organisation under one KvK is legitimate — a practice running two legal entities, or a deliberately separate environment — which is why neither column carries a uniqueness constraint and neither should.  Comparison is on the normalised value, so a stored `03-012345` answers for a requested `03012345`. An omitted value is not asked about and its answer is `false`.  A POST rather than a GET because a query parameter travels in the request line, which every access log along the way records (ADR-016). 
         * @summary Check whether a KvK-nummer or AGB-code is already in use
         * @param {OrganizationIdentifierCheckRequest} organizationIdentifierCheckRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async checkOrganizationIdentifiers(organizationIdentifierCheckRequest: OrganizationIdentifierCheckRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OrganizationIdentifierCheckResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.checkOrganizationIdentifiers(organizationIdentifierCheckRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationsApi.checkOrganizationIdentifiers']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Creates a new organization and assigns the current user as administrator. This is used for organization onboarding when a user doesn\'t have an organization yet. 
         * @summary Create organization
         * @param {CreateOrganizationRequest} createOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createOrganization(createOrganizationRequest: CreateOrganizationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OrganizationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createOrganization(createOrganizationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationsApi.createOrganization']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Removes the current organization\'s logo. Requires `organization.settings.update` permission. 
         * @summary Delete organization logo
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteOrganizationLogo(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteOrganizationLogo(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationsApi.deleteOrganizationLogo']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a stable `{ behandelaarId: paletteIndex }` map for the current user\'s organization. Palette indices are assigned lazily and persisted server-side, so a behandelaar keeps the same colour across sessions, users, and member (de)selection. Any behandelaar without an index yet is assigned the lowest free palette index (0-based) and the map is persisted before it is returned.  The colour palette itself lives in the frontend; the backend only stores and serves the integer index.  Org-scoped: any authenticated organization member may read. 
         * @summary Get per-behandelaar agenda colours
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getAgendaColors(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AgendaColorsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getAgendaColors(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationsApi.getAgendaColors']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the organization of the currently authenticated user
         * @summary Get current organization
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCurrentOrganization(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OrganizationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCurrentOrganization(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationsApi.getCurrentOrganization']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the four things a newly created practice still has to do before Scrivio is useful, each with a derived completion flag.  Completion is **derived on every request** and never stored: there is no acknowledgement state, so an item cannot disagree with what the organization actually has, and it ticks itself as the practice works.  Items seeded by organization creation — afspraaktypes, briefsjablonen, the default locatie — are deliberately absent: they are green before the practice has logged in and are therefore not tasks. So are cliëntportaal and afspraakherinneringen, which are always on, and declaratiestromen (`enabledFinancingRegimes`): Zvw is on without asking, and only a practice that also works under the Jeugdwet has anything to switch on.  Org-scoped: any authenticated organization member may read. It discloses nothing a member cannot already see, so it needs no permission. 
         * @summary Get the \"aan de slag\" checklist for the current organization
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getOnboardingChecklist(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OnboardingChecklistResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getOnboardingChecklist(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationsApi.getOnboardingChecklist']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Retrieves the current organization\'s logo image. Streams the image bytes with the correct Content-Type header. 
         * @summary Get organization logo
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getOrganizationLogo(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getOrganizationLogo(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationsApi.getOrganizationLogo']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Each Scrivio agreement at the version currently published, and the page that version is published on (#4490). The create-organisation form links these and sends the versions back in `CreateOrganizationRequest.acceptedAgreements`, so neither a version nor a link is ever typed into a client.  Open to any authenticated user: the caller is a practice about to sign itself up, which has no organisation for `OrganizationResponse.agreements` to answer about.  The link is derived from the version — each edition has a page of its own that a later one never overwrites — so the text a practice reads is the text its acceptance names. 
         * @summary The agreements a practice accepts by creating an organisation
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getPublishedAgreements(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<PublishedAgreement>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getPublishedAgreements(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationsApi.getPublishedAgreements']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates the organization of the currently authenticated user. Requires `organization.settings.update` permission. 
         * @summary Update current organization
         * @param {UpdateOrganizationRequest} updateOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateCurrentOrganization(updateOrganizationRequest: UpdateOrganizationRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OrganizationResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateCurrentOrganization(updateOrganizationRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationsApi.updateCurrentOrganization']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Uploads a new logo for the current organization. Accepts PNG, JPEG, or SVG images up to 2MB. Recommended dimensions: 300x100 pixels. Requires `organization.settings.update` permission. 
         * @summary Upload organization logo
         * @param {File} file Logo image file (PNG, JPEG, or SVG, max 2MB)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async uploadOrganizationLogo(file: File, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<OrganizationLogoResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.uploadOrganizationLogo(file, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationsApi.uploadOrganizationLogo']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * OrganizationsApi - factory interface
 */
export const OrganizationsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = OrganizationsApiFp(configuration)
    return {
        /**
         * Records that the organisation accepts the currently published version of each agreement it is not yet bound by (#4494) — the *Akkoord* of the prompt a user holding `organization.settings.update` is shown after sign-in while `OrganizationResponse.agreements` reports a `currentVersionOutstanding`.  The body names what the prompt showed. **Every outstanding document must be present at its current version, and no document may name another version**; otherwise `400` with `code: agreement-acceptance-invalid` and nothing is recorded — a prompt left open while a new version was published has to reload and ask again.  A document that is not outstanding is not recorded again, so a second beheerder answering the same prompt records nothing and still gets `200`. Each document that is recorded is a new acceptance: `REACCEPTANCE` where the organisation had accepted an earlier version, `SELF_SIGNUP` where it had accepted nothing. The party details are the organisation\'s as they stand.  Nothing is gated on this: an organisation that never answers keeps working under the version it accepted. 
         * @summary Accept the current version of every outstanding agreement
         * @param {AcceptAgreementsRequest} acceptAgreementsRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        acceptAgreements(acceptAgreementsRequest: AcceptAgreementsRequest, options?: RawAxiosRequestConfig): AxiosPromise<Array<AgreementStatus>> {
            return localVarFp.acceptAgreements(acceptAgreementsRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Answers whether an organisation in Scrivio already carries the given KvK-nummer or AGB-code, so the create-organisation form can warn a practice that appears to be here already: a colleague signs up from the website not knowing a beheerder was invited, and the practice ends up with two organisations splitting its cliënten, declaraties and contracten (#4438).  **The answer is yes or no and nothing else.** No name, no id, no contact. The caller typically has no organisation of their own, so anything more would turn this into a directory of the practices on the platform. It is bounded further by a per-user rate limit, and the 429 says nothing about whether anything matched.  It is a **warning, not a gate**. `POST /api/v1/organizations` does not consult this and refuses nothing on account of it: a second organisation under one KvK is legitimate — a practice running two legal entities, or a deliberately separate environment — which is why neither column carries a uniqueness constraint and neither should.  Comparison is on the normalised value, so a stored `03-012345` answers for a requested `03012345`. An omitted value is not asked about and its answer is `false`.  A POST rather than a GET because a query parameter travels in the request line, which every access log along the way records (ADR-016). 
         * @summary Check whether a KvK-nummer or AGB-code is already in use
         * @param {OrganizationIdentifierCheckRequest} organizationIdentifierCheckRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        checkOrganizationIdentifiers(organizationIdentifierCheckRequest: OrganizationIdentifierCheckRequest, options?: RawAxiosRequestConfig): AxiosPromise<OrganizationIdentifierCheckResponse> {
            return localVarFp.checkOrganizationIdentifiers(organizationIdentifierCheckRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Creates a new organization and assigns the current user as administrator. This is used for organization onboarding when a user doesn\'t have an organization yet. 
         * @summary Create organization
         * @param {CreateOrganizationRequest} createOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createOrganization(createOrganizationRequest: CreateOrganizationRequest, options?: RawAxiosRequestConfig): AxiosPromise<OrganizationResponse> {
            return localVarFp.createOrganization(createOrganizationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Removes the current organization\'s logo. Requires `organization.settings.update` permission. 
         * @summary Delete organization logo
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteOrganizationLogo(options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteOrganizationLogo(options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a stable `{ behandelaarId: paletteIndex }` map for the current user\'s organization. Palette indices are assigned lazily and persisted server-side, so a behandelaar keeps the same colour across sessions, users, and member (de)selection. Any behandelaar without an index yet is assigned the lowest free palette index (0-based) and the map is persisted before it is returned.  The colour palette itself lives in the frontend; the backend only stores and serves the integer index.  Org-scoped: any authenticated organization member may read. 
         * @summary Get per-behandelaar agenda colours
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getAgendaColors(options?: RawAxiosRequestConfig): AxiosPromise<AgendaColorsResponse> {
            return localVarFp.getAgendaColors(options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the organization of the currently authenticated user
         * @summary Get current organization
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCurrentOrganization(options?: RawAxiosRequestConfig): AxiosPromise<OrganizationResponse> {
            return localVarFp.getCurrentOrganization(options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the four things a newly created practice still has to do before Scrivio is useful, each with a derived completion flag.  Completion is **derived on every request** and never stored: there is no acknowledgement state, so an item cannot disagree with what the organization actually has, and it ticks itself as the practice works.  Items seeded by organization creation — afspraaktypes, briefsjablonen, the default locatie — are deliberately absent: they are green before the practice has logged in and are therefore not tasks. So are cliëntportaal and afspraakherinneringen, which are always on, and declaratiestromen (`enabledFinancingRegimes`): Zvw is on without asking, and only a practice that also works under the Jeugdwet has anything to switch on.  Org-scoped: any authenticated organization member may read. It discloses nothing a member cannot already see, so it needs no permission. 
         * @summary Get the \"aan de slag\" checklist for the current organization
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOnboardingChecklist(options?: RawAxiosRequestConfig): AxiosPromise<OnboardingChecklistResponse> {
            return localVarFp.getOnboardingChecklist(options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieves the current organization\'s logo image. Streams the image bytes with the correct Content-Type header. 
         * @summary Get organization logo
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrganizationLogo(options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.getOrganizationLogo(options).then((request) => request(axios, basePath));
        },
        /**
         * Each Scrivio agreement at the version currently published, and the page that version is published on (#4490). The create-organisation form links these and sends the versions back in `CreateOrganizationRequest.acceptedAgreements`, so neither a version nor a link is ever typed into a client.  Open to any authenticated user: the caller is a practice about to sign itself up, which has no organisation for `OrganizationResponse.agreements` to answer about.  The link is derived from the version — each edition has a page of its own that a later one never overwrites — so the text a practice reads is the text its acceptance names. 
         * @summary The agreements a practice accepts by creating an organisation
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getPublishedAgreements(options?: RawAxiosRequestConfig): AxiosPromise<Array<PublishedAgreement>> {
            return localVarFp.getPublishedAgreements(options).then((request) => request(axios, basePath));
        },
        /**
         * Updates the organization of the currently authenticated user. Requires `organization.settings.update` permission. 
         * @summary Update current organization
         * @param {UpdateOrganizationRequest} updateOrganizationRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateCurrentOrganization(updateOrganizationRequest: UpdateOrganizationRequest, options?: RawAxiosRequestConfig): AxiosPromise<OrganizationResponse> {
            return localVarFp.updateCurrentOrganization(updateOrganizationRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Uploads a new logo for the current organization. Accepts PNG, JPEG, or SVG images up to 2MB. Recommended dimensions: 300x100 pixels. Requires `organization.settings.update` permission. 
         * @summary Upload organization logo
         * @param {File} file Logo image file (PNG, JPEG, or SVG, max 2MB)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        uploadOrganizationLogo(file: File, options?: RawAxiosRequestConfig): AxiosPromise<OrganizationLogoResponse> {
            return localVarFp.uploadOrganizationLogo(file, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * OrganizationsApi - object-oriented interface
 */
export class OrganizationsApi extends BaseAPI {
    /**
     * Records that the organisation accepts the currently published version of each agreement it is not yet bound by (#4494) — the *Akkoord* of the prompt a user holding `organization.settings.update` is shown after sign-in while `OrganizationResponse.agreements` reports a `currentVersionOutstanding`.  The body names what the prompt showed. **Every outstanding document must be present at its current version, and no document may name another version**; otherwise `400` with `code: agreement-acceptance-invalid` and nothing is recorded — a prompt left open while a new version was published has to reload and ask again.  A document that is not outstanding is not recorded again, so a second beheerder answering the same prompt records nothing and still gets `200`. Each document that is recorded is a new acceptance: `REACCEPTANCE` where the organisation had accepted an earlier version, `SELF_SIGNUP` where it had accepted nothing. The party details are the organisation\'s as they stand.  Nothing is gated on this: an organisation that never answers keeps working under the version it accepted. 
     * @summary Accept the current version of every outstanding agreement
     * @param {AcceptAgreementsRequest} acceptAgreementsRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public acceptAgreements(acceptAgreementsRequest: AcceptAgreementsRequest, options?: RawAxiosRequestConfig) {
        return OrganizationsApiFp(this.configuration).acceptAgreements(acceptAgreementsRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Answers whether an organisation in Scrivio already carries the given KvK-nummer or AGB-code, so the create-organisation form can warn a practice that appears to be here already: a colleague signs up from the website not knowing a beheerder was invited, and the practice ends up with two organisations splitting its cliënten, declaraties and contracten (#4438).  **The answer is yes or no and nothing else.** No name, no id, no contact. The caller typically has no organisation of their own, so anything more would turn this into a directory of the practices on the platform. It is bounded further by a per-user rate limit, and the 429 says nothing about whether anything matched.  It is a **warning, not a gate**. `POST /api/v1/organizations` does not consult this and refuses nothing on account of it: a second organisation under one KvK is legitimate — a practice running two legal entities, or a deliberately separate environment — which is why neither column carries a uniqueness constraint and neither should.  Comparison is on the normalised value, so a stored `03-012345` answers for a requested `03012345`. An omitted value is not asked about and its answer is `false`.  A POST rather than a GET because a query parameter travels in the request line, which every access log along the way records (ADR-016). 
     * @summary Check whether a KvK-nummer or AGB-code is already in use
     * @param {OrganizationIdentifierCheckRequest} organizationIdentifierCheckRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public checkOrganizationIdentifiers(organizationIdentifierCheckRequest: OrganizationIdentifierCheckRequest, options?: RawAxiosRequestConfig) {
        return OrganizationsApiFp(this.configuration).checkOrganizationIdentifiers(organizationIdentifierCheckRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Creates a new organization and assigns the current user as administrator. This is used for organization onboarding when a user doesn\'t have an organization yet. 
     * @summary Create organization
     * @param {CreateOrganizationRequest} createOrganizationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createOrganization(createOrganizationRequest: CreateOrganizationRequest, options?: RawAxiosRequestConfig) {
        return OrganizationsApiFp(this.configuration).createOrganization(createOrganizationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Removes the current organization\'s logo. Requires `organization.settings.update` permission. 
     * @summary Delete organization logo
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteOrganizationLogo(options?: RawAxiosRequestConfig) {
        return OrganizationsApiFp(this.configuration).deleteOrganizationLogo(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a stable `{ behandelaarId: paletteIndex }` map for the current user\'s organization. Palette indices are assigned lazily and persisted server-side, so a behandelaar keeps the same colour across sessions, users, and member (de)selection. Any behandelaar without an index yet is assigned the lowest free palette index (0-based) and the map is persisted before it is returned.  The colour palette itself lives in the frontend; the backend only stores and serves the integer index.  Org-scoped: any authenticated organization member may read. 
     * @summary Get per-behandelaar agenda colours
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getAgendaColors(options?: RawAxiosRequestConfig) {
        return OrganizationsApiFp(this.configuration).getAgendaColors(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the organization of the currently authenticated user
     * @summary Get current organization
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCurrentOrganization(options?: RawAxiosRequestConfig) {
        return OrganizationsApiFp(this.configuration).getCurrentOrganization(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the four things a newly created practice still has to do before Scrivio is useful, each with a derived completion flag.  Completion is **derived on every request** and never stored: there is no acknowledgement state, so an item cannot disagree with what the organization actually has, and it ticks itself as the practice works.  Items seeded by organization creation — afspraaktypes, briefsjablonen, the default locatie — are deliberately absent: they are green before the practice has logged in and are therefore not tasks. So are cliëntportaal and afspraakherinneringen, which are always on, and declaratiestromen (`enabledFinancingRegimes`): Zvw is on without asking, and only a practice that also works under the Jeugdwet has anything to switch on.  Org-scoped: any authenticated organization member may read. It discloses nothing a member cannot already see, so it needs no permission. 
     * @summary Get the \"aan de slag\" checklist for the current organization
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getOnboardingChecklist(options?: RawAxiosRequestConfig) {
        return OrganizationsApiFp(this.configuration).getOnboardingChecklist(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieves the current organization\'s logo image. Streams the image bytes with the correct Content-Type header. 
     * @summary Get organization logo
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getOrganizationLogo(options?: RawAxiosRequestConfig) {
        return OrganizationsApiFp(this.configuration).getOrganizationLogo(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Each Scrivio agreement at the version currently published, and the page that version is published on (#4490). The create-organisation form links these and sends the versions back in `CreateOrganizationRequest.acceptedAgreements`, so neither a version nor a link is ever typed into a client.  Open to any authenticated user: the caller is a practice about to sign itself up, which has no organisation for `OrganizationResponse.agreements` to answer about.  The link is derived from the version — each edition has a page of its own that a later one never overwrites — so the text a practice reads is the text its acceptance names. 
     * @summary The agreements a practice accepts by creating an organisation
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getPublishedAgreements(options?: RawAxiosRequestConfig) {
        return OrganizationsApiFp(this.configuration).getPublishedAgreements(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates the organization of the currently authenticated user. Requires `organization.settings.update` permission. 
     * @summary Update current organization
     * @param {UpdateOrganizationRequest} updateOrganizationRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateCurrentOrganization(updateOrganizationRequest: UpdateOrganizationRequest, options?: RawAxiosRequestConfig) {
        return OrganizationsApiFp(this.configuration).updateCurrentOrganization(updateOrganizationRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Uploads a new logo for the current organization. Accepts PNG, JPEG, or SVG images up to 2MB. Recommended dimensions: 300x100 pixels. Requires `organization.settings.update` permission. 
     * @summary Upload organization logo
     * @param {File} file Logo image file (PNG, JPEG, or SVG, max 2MB)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public uploadOrganizationLogo(file: File, options?: RawAxiosRequestConfig) {
        return OrganizationsApiFp(this.configuration).uploadOrganizationLogo(file, options).then((request) => request(this.axios, this.basePath));
    }
}



